import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {CIRCLE_BRUSH, PENCIL_BRUSH, SPRAY_BRUSH} from '@PosterWhiteboard/models/draw.class';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import pencil from '@Assets/postermaker/draw-svgs/pencil.svg';
import circle from '@Assets/postermaker/draw-svgs/circle.svg';
import spray from '@Assets/postermaker/draw-brush-spray-stroke.png';
import {useAppSelector} from '@/hooks';

interface PosterEditorDrawBrushPanelProps {
  panelId: string;
}

export default function PosterEditorDrawBrushPanel({...props}: PosterEditorDrawBrushPanelProps): ReactElement {
  const onClose = useDefaultPanelClose(props.panelId);
  const drawObject = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing;
  });

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_brush')} />;
  };

  const updateBrushType = (id: string): void => {
    if (drawObject?.drawBrush?.brush === id) {
      return;
    }
    window.posterEditor?.whiteboard?.drawing.updateBrushType(id);
  };

  const getContent = (): ReactElement => {
    return (
      <ul className="flex-h-row spacing-p-l-4 spacing-p-r-4 spacing-p-t-1 spacing-p-b-4">
        <ContextualMenuThumbnailItem
          thumbnail={
            <Thumbnail
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: pencil as string,
              }}
              className="radius-2"
            />
          }
          isSelected={drawObject?.drawBrush?.brush === PENCIL_BRUSH}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_pen')}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            updateBrushType(PENCIL_BRUSH);
          }}
        />
        <ContextualMenuThumbnailItem
          thumbnail={
            <Thumbnail
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: circle as string,
              }}
              className="radius-2"
            />
          }
          isSelected={drawObject?.drawBrush?.brush === CIRCLE_BRUSH}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_circle')}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            updateBrushType(CIRCLE_BRUSH);
          }}
        />
        <ContextualMenuThumbnailItem
          thumbnail={
            <Thumbnail
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: spray as string,
              }}
              className="radius-2"
            />
          }
          isSelected={drawObject?.drawBrush?.brush === SPRAY_BRUSH}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_spray')}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            updateBrushType(SPRAY_BRUSH);
          }}
        />
      </ul>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
